import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { openAuthModal } from '@/store/slices/uiSlice';
import { useGetBetHistoryQuery } from '@/store/api/betApi';

import type { BrandFeature, TopNavMenuItem } from '@/brands';
import { brand, isMenuLinkPainted, useBrandFeatures, useBrandMenus } from '@/brands';

/**
 * One tab of the footer bar.
 *
 * Flat, in the reference site's shape: a bare icon with its label under it, the
 * active tab marked by a short rule under its label. Only the bet slip sits on
 * a plate.
 */
function NavItem({
  icon, label, isActive, onClick, accent, plate, badge,
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  accent?: string;
  plate?: boolean;
  badge?: number;
}) {
  const colour = accent ?? '#343131';
  return (
    <button
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className="relative flex flex-1 flex-col items-center justify-center gap-1 py-1 transition-all active:scale-95"
      style={{ color: colour, minWidth: 0 }}
    >
      {/* As on the reference: a short dark rule under the active tab's label. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-1/2 h-[2px] w-9 -translate-x-1/2 rounded-full bg-cb-ink transition-opacity"
        style={{ opacity: isActive ? 1 : 0 }}
      />
      {plate ? (
        // The bet slip is the one tab the reference raises onto a yellow plate,
        // larger than the bare icons and lifted above them, with its open-bet
        // count pinned to the corner. The 26px box keeps its label on the same
        // line as the others; the plate overflows it upwards.
        <span className="flex items-end justify-center" style={{ height: 26 }}>
          <span
            className="relative flex items-center justify-center rounded-[10px] bg-cb-yellow text-cb-ink shadow-sm"
            style={{ height: 38, width: 38 }}
          >
            {icon}
            {badge !== undefined && (
              <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-[#f10033] px-1 text-[10px] font-bold leading-none text-white">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </span>
        </span>
      ) : (
        <span className="flex items-center justify-center" style={{ height: 26 }}>
          {icon}
        </span>
      )}
      <span
        className="w-full truncate px-0.5 pb-1 text-center text-[11.5px] leading-none"
        style={{ fontWeight: isActive ? 600 : 500 }}
      >
        {label}
      </span>
    </button>
  );
}

type Tab = {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  /** Highlight colour, for the one tab the reference paints in its own colour. */
  accent?: string;
  /** Compiled into this build at all. */
  build?: keyof typeof brand.features;
  /** Granted to the brand by the super admin. */
  grant?: BrandFeature;
  /** Painted by the brand owner under Menu Visibility. */
  menu?: TopNavMenuItem;
  /** Extra paths that should also light this tab up. */
  match?: (pathname: string) => boolean;
  /** Sends a signed-out visitor to the signup sheet instead of `path`. */
  authOnly?: boolean;
  /** Drawn on a yellow plate with the open-bet count, as the reference's bet slip. */
  betSlip?: boolean;
};

// The reference's "Main" glyph: four rounded squares. Drawn in currentColor so
// the tab's active/idle colour still applies, unlike the source's fixed #343131.
const MainIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M8 3H6C5.60618 3.00006 5.21626 3.07793 4.85261 3.22911C4.48897 3.3803 4.15878 3.60184 3.881 3.881C3.60184 4.15878 3.3803 4.48897 3.22911 4.85261C3.07793 5.21626 3.00006 5.60618 3 6V8C3 8.825 3.337 9.575 3.881 10.119C4.15878 10.3982 4.48897 10.6197 4.85261 10.7709C5.21626 10.9221 5.60618 10.9999 6 11H8C8.39382 10.9999 8.78375 10.9221 9.14739 10.7709C9.51103 10.6197 9.84122 10.3982 10.119 10.119C10.3982 9.84122 10.6197 9.51103 10.7709 9.14739C10.9221 8.78375 10.9999 8.39382 11 8V6C10.9999 5.60618 10.9221 5.21626 10.7709 4.85261C10.6197 4.48897 10.3982 4.15878 10.119 3.881C9.84122 3.60184 9.51103 3.3803 9.14739 3.22911C8.78375 3.07793 8.39382 3.00006 8 3ZM18 3H16C15.6062 3.00006 15.2163 3.07793 14.8526 3.22911C14.489 3.3803 14.1588 3.60184 13.881 3.881C13.6018 4.15878 13.3803 4.48897 13.2291 4.85261C13.0779 5.21626 13.0001 5.60618 13 6V8C13 8.825 13.337 9.575 13.881 10.119C14.1588 10.3982 14.489 10.6197 14.8526 10.7709C15.2163 10.9221 15.6062 10.9999 16 11H18C18.3938 10.9999 18.7837 10.9221 19.1474 10.7709C19.511 10.6197 19.8412 10.3982 20.119 10.119C20.3982 9.84122 20.6197 9.51103 20.7709 9.14739C20.9221 8.78375 20.9999 8.39382 21 8V6C20.9999 5.60618 20.9221 5.21626 20.7709 4.85261C20.6197 4.48897 20.3982 4.15878 20.119 3.881C19.8412 3.60184 19.511 3.3803 19.1474 3.22911C18.7837 3.07793 18.3938 3.00006 18 3ZM8 13H6C5.60618 13.0001 5.21626 13.0779 4.85261 13.2291C4.48897 13.3803 4.15878 13.6018 3.881 13.881C3.60184 14.1588 3.3803 14.489 3.22911 14.8526C3.07793 15.2163 3.00006 15.6062 3 16V18C3 18.825 3.337 19.575 3.881 20.119C4.15878 20.3982 4.48897 20.6197 4.85261 20.7709C5.21626 20.9221 5.60618 20.9999 6 21H8C8.39382 20.9999 8.78375 20.9221 9.14739 20.7709C9.51103 20.6197 9.84122 20.3982 10.119 20.119C10.3982 19.8412 10.6197 19.511 10.7709 19.1474C10.9221 18.7837 10.9999 18.3938 11 18V16C10.9999 15.6062 10.9221 15.2163 10.7709 14.8526C10.6197 14.489 10.3982 14.1588 10.119 13.881C9.84122 13.6018 9.51103 13.3803 9.14739 13.2291C8.78375 13.0779 8.39382 13.0001 8 13ZM18 13H16C15.6062 13.0001 15.2163 13.0779 14.8526 13.2291C14.489 13.3803 14.1588 13.6018 13.881 13.881C13.6018 14.1588 13.3803 14.489 13.2291 14.8526C13.0779 15.2163 13.0001 15.6062 13 16V18C13 18.825 13.337 19.575 13.881 20.119C14.1588 20.3982 14.489 20.6197 14.8526 20.7709C15.2163 20.9221 15.6062 20.9999 16 21H18C18.3938 20.9999 18.7837 20.9221 19.1474 20.7709C19.511 20.6197 19.8412 20.3982 20.119 20.119C20.3982 19.8412 20.6197 19.511 20.7709 19.1474C20.9221 18.7837 20.9999 18.3938 21 18V16C20.9999 15.6062 20.9221 15.2163 20.7709 14.8526C20.6197 14.489 20.3982 14.1588 20.119 13.881C19.8412 13.6018 19.511 13.3803 19.1474 13.2291C18.7837 13.0779 18.3938 13.0001 18 13Z"
      fill="currentColor"
    />
  </svg>
);

// The reference's "Live" glyph: a stadium bowl with corner flags. currentColor,
// as with MainIcon, so the active/idle colour still applies.
const LiveIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M22 17.2519C21.9998 18.8386 18.6349 20.1635 14.1436 20.4892V16.7177C14.1433 16.2932 13.8236 15.9483 13.4297 15.9482H10.5713C10.1773 15.9483 9.85765 16.2931 9.85742 16.7177V20.4892C5.36498 20.1635 1.99902 18.8382 1.99902 17.2509V11.3232C3.90403 12.9613 8.05992 13.7275 12 13.7275C15.9401 13.7274 20.0952 12.9614 22 11.3232V17.2519ZM7.46094 15.7402C7.17433 15.6863 6.89057 15.8918 6.83789 16.206C6.78539 16.5201 6.97908 16.8206 7.27051 16.8769L7.62793 16.9423C7.92418 16.9912 8.19581 16.7745 8.24316 16.4638C8.28958 16.1493 8.09067 15.8528 7.79883 15.8017L7.46094 15.7402ZM16.541 15.7402L16.2031 15.8017C15.9112 15.8522 15.7125 16.1483 15.7588 16.4629C15.8052 16.7775 16.0801 16.9924 16.3721 16.9423L16.7295 16.8769C17.0209 16.8197 17.2152 16.5191 17.1631 16.206C17.11 15.8929 16.8337 15.6851 16.541 15.7402ZM5.04492 15.0664C4.76737 14.9554 4.45922 15.1077 4.35645 15.4062C4.25337 15.7044 4.39489 16.0376 4.67188 16.1484C4.79224 16.1969 4.91752 16.2429 5.0459 16.289C5.32488 16.3886 5.63073 16.2268 5.72363 15.9228C5.81694 15.6202 5.66362 15.2924 5.38281 15.1933C5.26725 15.152 5.15334 15.1101 5.04492 15.0664ZM19.6445 15.4052C19.5417 15.1067 19.2332 14.9553 18.9561 15.0654C18.8476 15.1092 18.7346 15.151 18.6182 15.1923C18.3365 15.2925 18.1844 15.6188 18.2764 15.9218C18.3671 16.219 18.6661 16.39 18.9531 16.289C19.0825 16.2433 19.2076 16.197 19.3281 16.1484C19.6053 16.0377 19.7468 15.7045 19.6445 15.4052ZM12 10.1767C15.6692 10.1768 17.9638 10.9961 18.666 11.6338C16.9055 12.1503 14.5706 12.4687 12 12.4687C9.42918 12.4687 7.09368 12.1503 5.33301 11.6338C6.03569 10.996 8.33052 10.1767 12 10.1767ZM13.4277 3.30172C13.8219 3.30172 14.1425 3.65291 14.1426 4.08492C14.1426 4.51703 13.8219 4.86813 13.4277 4.86813H12.7139V5.85543C14.5652 5.89949 16.2773 6.11398 17.7139 6.45211V5.51266L16.8896 5.33785C16.5021 5.25466 16.2492 4.84367 16.3242 4.41891C16.3994 3.9945 16.7721 3.71881 17.1611 3.79977L19.9668 4.39742C20.3542 4.47973 20.6062 4.89119 20.5312 5.31539C20.4549 5.74548 20.0751 6.01674 19.6943 5.93453L19.1426 5.81734V6.85934C20.9095 7.4676 22 8.30071 22 9.21969C21.9998 10.0163 21.1773 10.7463 19.8096 11.3232C18.9167 9.79151 15.4424 8.99313 11.999 8.99313C8.55572 8.99317 5.08231 9.79155 4.18945 11.3232C2.82168 10.7458 1.99918 10.0158 1.99902 9.21969C1.99902 8.30067 3.08943 7.46761 4.85645 6.85934V5.81734L4.30469 5.93453C3.92302 6.01666 3.54313 5.74598 3.4668 5.31637C3.39179 4.89209 3.64516 4.48061 4.03223 4.39742L6.83691 3.79977C7.22472 3.7171 7.59862 3.9954 7.67383 4.41891C7.74881 4.84305 7.49623 5.25448 7.10938 5.33785L6.28516 5.51266V6.45211C7.72177 6.11448 9.43384 5.89947 11.2852 5.85543V4.86813H10.5703C10.1763 4.8679 9.85645 4.51689 9.85645 4.08492C9.85654 3.65305 10.1763 3.30195 10.5703 3.30172H13.4277Z"
      fill="currentColor"
    />
  </svg>
);

// The reference's "Casino" glyph: a die behind a poker chip. currentColor, as
// with MainIcon, so the active/idle colour still applies.
const CasinoIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M14.3682 7.58594C16.1398 7.58597 17.805 8.28289 19.0576 9.5498C20.31 10.8168 21 12.5014 21 14.293C21 16.0844 20.3102 17.7683 19.0576 19.0352C17.805 20.3018 16.1398 21 14.3682 21C12.5965 21 10.9314 20.3021 9.67871 19.0352C8.42639 17.7685 7.73636 16.0848 7.73633 14.293C7.73633 12.5011 8.42602 10.8168 9.67871 9.5498C10.9314 8.28325 12.5965 7.58594 14.3682 7.58594ZM16.1377 18.0723C15.757 18.2555 15.3439 18.3817 14.9102 18.4395L14.7236 18.46L14.7275 20.1982C15.5406 20.1481 16.3095 19.9294 17 19.5762L16.1377 18.0723ZM9.49609 17.5635C9.93468 18.2297 10.5035 18.8017 11.165 19.2412L12.0195 17.7334C11.6155 17.4506 11.2638 17.0962 10.9814 16.6895L9.49609 17.5635ZM14.3682 10.8076C12.4681 10.8077 10.9229 12.3713 10.9229 14.293C10.9229 16.2146 12.4682 17.7772 14.3682 17.7773C16.2683 17.7773 17.8144 16.2147 17.8145 14.293C17.8145 12.3712 16.2683 10.8076 14.3682 10.8076ZM18.4902 14.6357C18.449 15.1441 18.3174 15.6276 18.1123 16.0684L19.6016 16.9346C19.9481 16.2352 20.1615 15.4565 20.208 14.6338L18.4902 14.6357ZM9.12793 11.6641C8.78326 12.3644 8.5717 13.1438 8.52734 13.9668L10.2461 13.96C10.2811 13.5151 10.3846 13.0895 10.5469 12.6943L10.6201 12.5273L9.12793 11.6641ZM10.749 3C11.6738 3.00018 12.4258 3.7609 12.4258 4.69629V7.14648C12.1179 7.23181 11.8177 7.33636 11.5273 7.45996C11.5203 7.46305 11.5129 7.46567 11.5059 7.46875C9.6815 8.25254 8.23623 9.76309 7.52441 11.6406C7.49274 11.7243 7.4622 11.8086 7.43359 11.8936C7.36279 12.1025 7.30136 12.3162 7.24902 12.5332H4.67676C3.75197 12.533 3 11.7723 3 10.8369V4.69629C3 3.76092 3.75197 3.00021 4.67676 3H10.749ZM16.7246 10.8584C17.128 11.1421 17.4796 11.4966 17.7607 11.9043L19.248 11.0342C18.811 10.3669 18.2437 9.79396 17.583 9.35254L16.7246 10.8584ZM5.92676 8.45996C5.31725 8.45996 4.82333 8.95682 4.82324 9.57324C4.82324 10.1897 5.3172 10.6865 5.92676 10.6865C6.53323 10.6865 7.02734 10.1897 7.02734 9.57324C7.02726 8.95682 6.53626 8.45997 5.92676 8.45996ZM14.0234 8.38672C13.1999 8.43531 12.4215 8.65719 11.7236 9.0166L12.5889 10.5176C13.0296 10.3045 13.514 10.1677 14.0234 10.125V8.38672ZM5.92676 4.84375C5.3172 4.84375 4.82324 5.34053 4.82324 5.95703C4.82328 6.5735 5.31722 7.07031 5.92676 7.07031C6.53321 7.0703 7.0273 6.57038 7.02734 5.95703C7.02734 5.34365 6.53631 4.84376 5.92676 4.84375ZM9.50195 4.84375C8.89249 4.84386 8.39844 5.3406 8.39844 5.95703C8.39848 6.57343 8.89252 7.0702 9.50195 7.07031C10.1084 7.07031 10.6025 6.57038 10.6025 5.95703C10.6025 5.34365 10.1115 4.84375 9.50195 4.84375Z"
      fill="currentColor"
    />
  </svg>
);

// The reference's "Live Dealers" glyph: a long-haired dealer bust. currentColor,
// as with MainIcon, so the active/idle colour still applies.
const DealersIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M18.7756 13.1147C18.5352 12.7158 18.1265 12.442 17.6377 12.3794C17.5896 12.3716 17.5415 12.3559 17.4854 12.3481V8.49935C17.4774 5.51108 15.0733 3.08605 12.0601 3C12.012 3 11.9639 3 11.9159 3C8.84659 3 6.35432 5.47197 6.35432 8.50717V12.3638C6.35432 12.3638 6.2822 12.3872 6.24213 12.4029C6.12192 12.4342 6.00973 12.4654 5.89754 12.5124C5.88952 12.5124 5.8735 12.528 5.86548 12.528C5.76131 12.575 5.66514 12.6454 5.57699 12.708C5.22438 12.9817 5 13.3963 5 13.8735C5 14.0613 5.04007 14.2334 5.10418 14.3977C5.32055 14.9531 5.85747 15.352 6.49857 15.3755C6.11391 15.6493 5.76131 16.1421 5.67315 17.0104C5.65713 17.1199 5.64911 17.2216 5.64911 17.3312V17.9257C5.64911 18.5515 5.96966 19.1382 6.50658 19.4668C8.07728 20.4368 9.93646 21 11.9319 21C13.9273 21 15.8105 20.4289 17.3892 19.4511C17.9021 19.1304 18.2147 18.5671 18.2147 17.9726V17.339C18.2147 17.2295 18.2147 17.1199 18.1906 17.0183C18.1025 16.1421 17.7418 15.6493 17.3572 15.3755C17.3973 15.3755 17.4293 15.3833 17.4614 15.3833C18.2307 15.3833 18.8638 14.8279 18.976 14.116C18.992 14.0378 19 13.9674 19 13.8814C19 13.6076 18.9199 13.3494 18.7836 13.1304L18.7756 13.1147ZM8.99886 8.8983C10.265 8.71056 11.3469 8.26467 11.988 7.2399C12.597 8.20209 13.5827 8.64016 14.7607 8.87484V10.588C14.7607 12.1369 13.4625 13.3963 11.8758 13.3963C10.2891 13.3963 8.99886 12.1369 8.99886 10.588V8.8983ZM12.1963 19.9909C12.0761 20.2099 11.7636 20.2099 11.6434 19.9909L9.15112 15.266H10.5215C10.7218 15.2269 10.8821 15.0626 10.8821 14.8592V13.8814C11.1946 13.9674 11.5152 14.03 11.8598 14.03C12.2444 14.03 12.605 13.9518 12.9416 13.8501V14.8592C12.9416 15.0313 13.0538 15.1799 13.2141 15.2347L13.3343 15.2581H13.3503H14.6726L12.1803 19.983L12.1963 19.9909Z"
      fill="currentColor"
    />
  </svg>
);

// The reference's "Aviator" glyph: a biplane. currentColor, so the tab's red
// `accent` paints it.
const AviatorIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21.4777 6.80842C21.1803 6.86372 20.9868 7.14968 21.0418 7.44555L21.6849 10.9048C21.6849 10.9048 21.6776 10.9062 21.6751 10.9066L20.9794 11.036L20.8488 10.3333C20.7374 9.73411 20.1629 9.33842 19.5655 9.44947C19.1575 9.52533 18.8486 9.81999 18.7266 10.1896C18.5729 10.0881 18.3837 10.0416 18.1895 10.0777L17.3833 10.2276L16.962 7.96172L18.7097 7.63679C19.253 7.53579 19.6129 7.00972 19.5116 6.46482C19.4103 5.91992 18.8853 5.55837 18.3421 5.65937L9.66487 7.2726C9.12162 7.3736 8.76166 7.89967 8.86297 8.44457C8.96427 8.98946 9.48925 9.35101 10.0325 9.25001L11.7925 8.9228L12.2138 11.1887L5.19826 12.493L2.89091 9.87626C2.5598 9.50418 2.0652 9.3334 1.58341 9.42297C0.814007 9.56602 0.310072 10.3025 0.460006 11.0679L1.13255 14.6033C1.3072 15.5427 2.07421 16.2623 3.0226 16.3845L6.40658 16.8037C7.89853 16.9906 9.42633 16.9463 10.9037 16.6717L17.4448 15.4556L17.5245 15.8846C17.1969 16.147 17.0144 16.5738 17.0969 17.0176C17.2156 17.6562 17.8281 18.078 18.4647 17.9596C19.1014 17.8413 19.5213 17.2275 19.4026 16.5889C19.3063 16.0711 18.8815 15.7012 18.387 15.6401L18.3223 15.2924L19.1286 15.1425C19.3228 15.1064 19.4827 14.9951 19.5896 14.8451C19.8363 15.1462 20.2304 15.3101 20.6385 15.2342C21.2358 15.1232 21.6297 14.5474 21.5183 13.9483L21.3442 13.0114L22.0398 12.882C22.0398 12.882 22.0472 12.8807 22.0496 12.8802L22.6905 16.3271C22.7459 16.6255 23.0313 16.8198 23.3263 16.765C23.6213 16.7102 23.8172 16.4237 23.7622 16.1279L22.1111 7.24675C22.0556 6.94841 21.7703 6.75404 21.4753 6.80888L21.4777 6.80842ZM12.6652 8.76056L16.082 8.12533L16.5032 10.3912L13.0864 11.0265L12.6652 8.76056Z"
      fill="currentColor"
    />
  </svg>
);

// The reference's "Bet slip" glyph: a ruled slip. Its own 14x18 box, so it is
// sized by height to sit inside the tab's plate.
const BetSlipIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={(size * 14) / 18} height={size} viewBox="0 0 14 18" fill="none" aria-hidden="true">
    <path
      d="M11.9737 1.89471H2.02632C0.908526 1.89471 0 2.80772 0 3.93102V15.9637C0 17.0871 0.908526 18 2.02632 18H11.9737C13.0915 18 14 17.0871 14 15.9637V3.93102C14 2.80772 13.0915 1.89471 11.9737 1.89471ZM2.72433 6.15244H7.36842C7.77516 6.15244 8.10526 6.48417 8.10526 6.89292C8.10526 7.30166 7.77516 7.63339 7.36842 7.63339H2.72433C2.31759 7.63339 1.98748 7.30166 1.98748 6.89292C1.98748 6.48417 2.31759 6.15244 2.72433 6.15244ZM11.0527 13.5572H2.72433C2.31759 13.5572 1.98748 13.2255 1.98748 12.8167C1.98748 12.408 2.31759 12.0763 2.72433 12.0763H11.0527C11.4593 12.0763 11.7895 12.408 11.7895 12.8167C11.7895 13.2255 11.4593 13.5572 11.0527 13.5572ZM11.0527 10.5953H2.72433C2.31759 10.5953 1.98748 10.2635 1.98748 9.85477C1.98748 9.44607 2.31759 9.11434 2.72433 9.11434H11.0527C11.4593 9.11434 11.7895 9.44607 11.7895 9.85477C11.7895 10.2635 11.4593 10.5953 11.0527 10.5953Z"
      fill="currentColor"
    />
  </svg>
);

const allTabs: Tab[] = [
  {
    key: 'main', label: 'Main', path: '/home',
    icon: <MainIcon />, menu: 'home',
    match: (p) => p === '/' || p === '/home',
  },
  {
    key: 'live', label: 'Live', path: '/inplay',
    icon: <LiveIcon />, grant: 'sports', menu: 'inplay',
  },
  {
    key: 'casino', label: 'Casino', path: '/casino/all',
    icon: <CasinoIcon />, build: 'aggregator', grant: 'casino', menu: 'casino',
    match: (p) => p === '/casino/all',
  },
  {
    key: 'dealers', label: 'Live Dealers', path: '/casino/all/live',
    icon: <DealersIcon />, build: 'aggregator', grant: 'casino', menu: 'liveCasino',
  },
  {
    key: 'aviator', label: 'Aviator', path: '/casino/all/crash',
    // The reference gives its crash game the one coloured tab in the bar.
    icon: <AviatorIcon />,
    accent: '#f10033',
    build: 'aggregator', grant: 'casino', menu: 'crash',
  },
  {
    // Open bets live on the bet-history page; a visitor has none, so for them
    // the tab opens the signup sheet instead. Last in the bar, as on the reference.
    key: 'betslip', label: 'Bet slip', path: '/user/bet-history',
    icon: <BetSlipIcon size={20} />, grant: 'sports', betSlip: true, authOnly: true,
  },
];

// The build half is a compile-time constant; the other two arrive with GET
// /brand, after first paint, so they are filtered in the component.
const buildTabs = allTabs.filter((t) => !t.build || brand.features[t.build]);

/**
 * Mobile footer navigation.
 *
 * Nothing sits above the tabs. This used to also carry a Deposit / Withdraw /
 * Wallet / Refer / WhatsApp / Telegram grid, which pushed the bar to roughly
 * 160px tall — on an iPhone that put the tabs themselves behind Safari's own
 * bottom chrome, so the nav was simply not reachable. Every one of those
 * shortcuts already exists elsewhere on the same screens.
 *
 * `env(safe-area-inset-bottom)` keeps the tabs clear of the home indicator on
 * notched devices; it resolves to 0 everywhere else.
 *
 * Shown at every width, not just on phones: the tabs are the quickest way
 * between the site's sections and there is no desktop equivalent in the header.
 */
const BottomNav = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isAuthed = useAppSelector((state) => state.auth.isAuthenticated);
  const menus = useBrandMenus();
  const features = useBrandFeatures();

  // Only the total is wanted, so one row is asked for. Shares the 'Bets' tag,
  // so placing a bet refreshes the badge.
  const { data: openBets } = useGetBetHistoryQuery(
    { betTime: 'CURRENT', limit: 1 },
    { skip: !isAuthed },
  );
  const openBetCount = openBets?.pagination?.totalItems ?? 0;

  // Same three-part test the top strip and the home tiles use, so a product the
  // super admin revoked cannot keep a tab pointing at a route FeatureGate 404s.
  const tabs = buildTabs.filter(
    (t) =>
      (!t.grant || features[t.grant] !== false) &&
      (!t.menu || isMenuLinkPainted(t.path, menus.topNav[t.menu] !== false, menus)),
  );

  if (!tabs.length) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{
        background: '#efeeee',
        // A near-white hairline let the bar melt into any pale page bottom.
        borderTop: '1px solid #d8d6d4',
        boxShadow: '0 -4px 16px rgba(52,49,49,0.16)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <nav
        className="flex items-stretch justify-around px-1 py-1.5"
        style={{ height: 'var(--cb-bottomnav-h, 58px)' }}
      >
        {tabs.map((t) => (
          <NavItem
            key={t.key}
            icon={t.icon}
            label={t.label}
            accent={t.accent}
            plate={t.betSlip}
            badge={t.betSlip ? openBetCount : undefined}
            isActive={t.match ? t.match(pathname) : pathname === t.path}
            onClick={() =>
              t.authOnly && !isAuthed ? dispatch(openAuthModal('register')) : navigate(t.path)
            }
          />
        ))}
      </nav>
    </div>
  );
};

export default BottomNav;
